/**
 * Specialty-based page access configuration.
 * Imported by both client and server to enforce consistent role-based navigation.
 */

export const READ_ONLY_SPECIALTIES = ["order_management", "account_manager"] as const;

/**
 * Which specialties can access each page route.
 * - `undefined` means all specialties (universal page)
 * - `string[]` means only those specialties (admin/manager always bypass)
 */
export const SPECIALTY_PAGE_ACCESS: Record<string, string[] | undefined> = {
  "/":                undefined,
  "/work-orders":     undefined,
  "/work-orders/:id": undefined,
  "/schedule":        ["press_operator", "embroidery_operator", "heat_press_operator", "polybag_operator"],
  "/lanes":           ["press_operator", "embroidery_operator", "heat_press_operator", "polybag_operator"],
  "/prep-steps":      ["art_department", "screen_burner", "ink_mixer", "press_operator", "embroidery_operator", "digitizer", "heat_press_operator", "hp_transfer_orderer", "product_handler", "quality_control", "polybag_operator"],
  "/screenmaster":    ["screen_burner"],
  "/inkmaster":       ["ink_mixer"],
  "/digitizing":      ["digitizer"],
  "/receiving":       ["product_handler", "order_management"],
  "/packing":         ["product_handler", "quality_control"],
};

/** Which machine types each specialty interacts with (for compact lanes on dashboard) */
export const SPECIALTY_MACHINE_TYPES: Record<string, string[]> = {
  press_operator: ["screenprint_press"],
  embroidery_operator: ["embroidery_machine"],
  heat_press_operator: ["heat_press"],
  polybag_operator: ["poly_bagger"],
};

/**
 * HUB-443: who may edit a run's *order* fields (Pantone colors, cost, quantity,
 * decoration, location, transfer info, coverage, thread, etc.).
 *
 * This mirrors the work-order-detail `canEditWO` rule exactly and is the single
 * source of truth shared by the client (to gate controls) and the server (to
 * enforce the write). Super admin / admin / manager, or the `art_department`
 * production-artist specialty.
 */
export interface RunOrderEditActor {
  isSuperAdmin?: boolean;
  isAdmin?: boolean;
  isManager?: boolean;
  specialties?: readonly string[];
}

export function canEditRunOrderFields(actor: RunOrderEditActor): boolean {
  if (actor.isSuperAdmin || actor.isAdmin || actor.isManager) return true;
  return (actor.specialties ?? []).includes("art_department");
}

/**
 * Operational run fields that non-editing production users legitimately update
 * through the shared run-update endpoint (e.g. operators saving a time estimate
 * from Schedule/Lanes, a press-setup plan, production notes, the shared Deco
 * Notes annotation, or per-color ink coverage that drives batch sizing). Anything
 * NOT in this allowlist is an order field and requires `canEditRunOrderFields`.
 *
 * `notes` (Deco Notes) and `coverage` are intentionally operational: both are
 * shop-floor fields that were editable by every write-access user before HUB-443
 * and are left ungated in the run UI. Treating them as order fields would regress
 * operators with a dead-end 403 (Bugbot PR #209, Finding 2). Read-only staff are
 * still blocked from all of these by the route's write-access gate.
 *
 * Secure-by-default: a run field added in the future is treated as an order
 * field (edit-access required) until it is explicitly added here.
 */
export const RUN_OPERATIONAL_FIELDS = [
  "manualTimeEstimate",
  "pressSetupPlan",
  "productionNotes",
  "notes",
  "coverage",
] as const;

export function runUpdateRequiresOrderEditAccess(fields: readonly string[]): boolean {
  const operational = new Set<string>(RUN_OPERATIONAL_FIELDS as readonly string[]);
  return fields.some((field) => !operational.has(field));
}
